import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Button, Card, PageHeader, Table } from "@cofano/cck";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language, usePermission } from "imports/Utils";

import { GetListPaymentTermsDocument, GetListPaymentTermsQuery } from "__generated__/view/paymentterms/graphql/queries.generated";
import { DeletePaymentTermDocument } from "__generated__/view/paymentterms/graphql/mutations.generated";
import { ChildPropsFromArray } from "types/commons";

type PaymentTermFromQuery = ChildPropsFromArray<GetListPaymentTermsQuery, "listPaymentTerms">

const PaymentTerms = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();
	const { isReadOnly } = usePermission();

	const { data, refetch, loading } = useQuery(GetListPaymentTermsDocument);

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.PaymentTerms,
		content: data?.listPaymentTerms || [],
	});

	const [ deletePaymentTerm, { loading: deleteLoading } ] = useMutation(DeletePaymentTermDocument);

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("PAYMENT_TERMS")}
					<Helmet title={intl.formatMessage({ id: "PAYMENT_TERMS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/paymentterms/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading || deleteLoading}
					columns={[ {
						id: "days",
						title: intl.formatMessage({ id: "PAYMENT_TERM_DAYS" }),
						sortable: true,
						filterComponent: "NUMBER",
					}, {
						id: "externalReference",
						title: intl.formatMessage({ id: "PAYMENT_TERM_EXTERNAL_REFERENCE" }),
						render: (row: PaymentTermFromQuery) => row.externalReference !== null && row.externalReference?.trim().length !== 0 ? row.externalReference : Language("N/A"),
						filterComponent: "TEXT",
					}, {
						id: "isDefault",
						title: intl.formatMessage({ id: "PAYMENT_TERM_DEFAULT" }),
						render: (row: PaymentTermFromQuery) => row.isDefault ? Language("YES") : Language("NO"),
						filterComponent: "BOOLEAN",
					} ]}
					rowActions={[ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/paymentterms/${row.id}/edit`, { state: { paymentTerm: row } }),
					}, {
						icon: <DeleteIcon />,
						onClick: (row: PaymentTermFromQuery) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deletePaymentTerm({
									variables: {
										id: row.id,
									},
								}).then(() => {
									refetchAfterDelete();
									refetch();
								});
							});
						},
					} ]}  />
			</Card>
		</div>
	);
};

export default PaymentTerms;
